import { api, get } from "@/lib/api";
import { encryptFile, requireKey } from "@/features/crypto/ops";
import type { FileTargetType } from "./types";

export interface UploadOptions {
  description?: string;
  /** Link the uploaded files to this item right away. */
  linkType?: FileTargetType;
  linkId?: string;
}

/** Key scope that files uploaded to this workspace (or note) must be encrypted with, if any. */
export async function uploadScope(workspaceId: string, noteId?: string): Promise<string | null> {
  if (noteId) {
    const d = await get<{ note: { encryptionScopeId: string | null }; workspace: { encryptionScopeId?: string | null } }>(`/api/notes/${noteId}`);
    return d.note.encryptionScopeId ?? d.workspace.encryptionScopeId ?? null;
  }
  return (await get<{ workspaceScopeId: string | null }>(`/api/workspaces/${workspaceId}/encryption`)).workspaceScopeId;
}

/**
 * Multipart body for one file. For end-to-end encrypted notes/projects the file and its name,
 * type and size are encrypted here first; the server receives ciphertext and a placeholder name.
 */
export async function fileForm(f: File, scopeId: string | null, fields: Record<string, string | undefined> = {}) {
  const form = new FormData();
  if (scopeId) {
    requireKey(scopeId);
    const enc = await encryptFile(scopeId, new Uint8Array(await f.arrayBuffer()), { filename: f.name, mime: f.type || "application/octet-stream", size: f.size });
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && k !== "description") form.append(k, v);
    form.append("encrypted", "true");
    form.append("encMeta", enc.encMeta);
    form.append("keyProof", requireKey(scopeId).proof);
    form.append("file", enc.blob, "encrypted-file");
  } else {
    for (const [k, v] of Object.entries(fields)) if (v !== undefined) form.append(k, v);
    form.append("file", f, f.name);
  }
  return form;
}

/**
 * The single entry point for uploading files (Files area, note/record panels, tasks,
 * manuscripts, papers). One request per file; text fields precede the file part.
 *
 * Files uploaded for a note become owned by that note, so they follow the note's own
 * access rules (and its encryption scope); everything else lands in the workspace Files
 * area with a link to the item.
 */
export async function uploadFiles(workspaceId: string, files: File[], opts: UploadOptions = {}): Promise<{ id: string; filename: string }[]> {
  const out: { id: string; filename: string }[] = [];
  const toNote = opts.linkType === "note" && opts.linkId ? opts.linkId : undefined;
  const scopeId = await uploadScope(workspaceId, toNote);
  for (const f of files) {
    if (toNote) {
      const form = await fileForm(f, scopeId, { description: opts.description });
      const r = await api<{ attachment: { id: string; filename: string } }>(`/api/notes/${toNote}/attachments`, { form });
      out.push({ id: r.attachment.id, filename: r.attachment.filename });
      continue;
    }
    const form = await fileForm(f, scopeId, {
      description: opts.description,
      linkType: opts.linkType && opts.linkId ? opts.linkType : undefined,
      linkId: opts.linkType && opts.linkId ? opts.linkId : undefined,
    });
    const r = await api<{ files: { id: string; filename: string }[] }>(`/api/workspaces/${workspaceId}/files`, { form });
    out.push(...r.files.map((x) => ({ id: x.id, filename: x.filename })));
  }
  return out;
}
